import { Link } from 'react-router-dom';

import { CandleStrip } from '@/components/fx/CandleStrip';
import { CountUp } from '@/components/fx/CountUp';
import { ForecastCanvas } from '@/components/fx/ForecastCanvas';
import { Marquee } from '@/components/fx/Marquee';
import { Reveal } from '@/components/fx/Reveal';
import { Headline } from '@/components/fx/Headline';
import { Sparkline } from '@/components/fx/Sparkline';
import { TiltCard } from '@/components/fx/TiltCard';
import type { HomeVals } from '@/lib/vals/home';

/** Statements, not quotes — the site publishes no number it cannot source. */
const TICKER = [
  'Forecasts written first',
  'Scored later',
  'Published either way',
  'Twenty sections',
  'Seventeen connectors',
  'Two engines',
  'Zero fake numbers',
  'Quantiles, never a point',
  'A baseline travels with every forecast',
];

export function HomePage({ v }: { v: HomeVals }) {
  return (
    <main id="wft-main" tabIndex={-1}>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="hero">
        <ForecastCanvas className="hero__canvas" />

        <div className="shell hero__inner">
          <Reveal as="p" className="eyebrow hero__eyebrow">
            World Finance Terminal · v0.1.0
          </Reveal>

          <Headline
            as="h1"
            className="display display--hero hero__title"
            text="The world's open financial data, with a scored forecast on every series."
            highlight={['scored', 'forecast']}
          />

          <Reveal as="p" className="lede hero__lede" delay={620}>
            Twenty sections across tape, context, engine and desk. Every series can be sent to
            GodEngine — TimesFM 2.5 for the quantile forecast, the swarm resolver for branching
            scenarios — and every forecast is written down before the outcome exists.
          </Reveal>

          <Reveal className="hero__actions" delay={760}>
            <a href="/cockpit" className="btn btn--primary">Open the terminal</a>
            <Link to="/platform" className="btn btn--ghost">
              See what is bound
            </Link>
          </Reveal>

          <Reveal className="hero__meta" delay={900}>
            <span className="dot dot--live" /> Observed ink
            <span className="dot dot--engine" /> Engine ink
            <span className="dot dot--baseline" /> Baseline, dashed
            <em>axes unlabelled on purpose</em>
          </Reveal>
        </div>

        <div className="hero__scroll" aria-hidden="true">
          <span>Scroll</span>
          <i />
        </div>
      </section>

      {/* ── Ticker ───────────────────────────────────────────────────────── */}
      <div className="ticker hairline-t">
        <Marquee
          duration={48}
          items={TICKER.map((t, i) => (
            <>
              <i
                className={`ticker__mark ticker__mark--${i % 3 === 2 ? 'down' : 'up'}`}
                aria-hidden="true"
              />
              {t}
            </>
          ))}
        />
      </div>

      {/* ── Figures ──────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <div className="figures">
            {v.figures.map((f, i) => (
              <Reveal key={f.label} delay={i * 70}>
                <TiltCard className="figure glass">
                  <span className="figure__value">
                    <CountUp value={f.value} />
                  </span>
                  <span className="figure__label">{f.label}</span>
                  <span className="figure__note">{f.note}</span>
                  <Sparkline seed={i * 13 + 5} className="figure__spark" height={34} />
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Provenance ───────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              The rule that makes the rest possible
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="No number appears in this product that did not come from a named source."
              highlight={['named', 'source.']}
            />
            <Reveal as="p" className="lede" delay={220}>
              There is no sample data, no fixture data in a production build, and no placeholder
              that looks like a value. Where a connector does not exist yet, the panel says so and
              names the module that would feed it.
            </Reveal>
          </div>

          <div className="guards">
            {v.guards.map((g, i) => (
              <Reveal key={g.rule} delay={i * 90}>
                <div className="guard glass">
                  <span className="guard__index">{String(i + 1).padStart(2, '0')}</span>
                  <p className="guard__rule">{g.rule}</p>
                  <p className="guard__where">{g.where}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pipeline ─────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            Source to pixel
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="One path from a public source to a pixel."
            highlight={['pixel.']}
          />

          {/* A pulse running the width of the row, so the five stages read as
              one path rather than five cards. */}
          <div className="flow" aria-hidden="true" />

          <div className="pipeline">
            {v.pipeline.map((p, i) => (
              <Reveal key={p.n} delay={i * 110}>
                <TiltCard className="stage glass" max={4}>
                  <span className="stage__n" style={{ color: p.color }}>
                    {p.n}
                  </span>
                  <h3 className="stage__name">{p.stage}</h3>
                  <ul className="stage__items">
                    {p.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <code className="stage__note">{p.note}</code>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Engines ──────────────────────────────────────────────────────── */}
      <section className="section section--engines">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            The engine
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Two engines, one ink."
            highlight={['ink.']}
          />

          <div className="lanes">
            {v.lanes.map((lane, i) => (
              <Reveal key={lane.name} delay={i * 140}>
                <TiltCard className="lane glass" max={4}>
                  <span className="lane__bar" style={{ background: lane.color }} />
                  <h3 className="lane__name" style={{ color: lane.color }}>
                    {lane.name}
                  </h3>
                  <code className="lane__module">{lane.module}</code>
                  <p className="lane__body">{lane.body}</p>
                  <p className="lane__feeds">
                    <span>Feeds</span> {lane.feeds}
                  </p>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          <Reveal className="inks">
            {v.engineInk.map((ink) => (
              <div key={ink.label} className="ink">
                <span className="ink__swatch" style={{ background: ink.color }} />
                <span className="ink__label">{ink.label}</span>
                <span className="ink__note">{ink.note}</span>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* ── Market band ──────────────────────────────────────────────────── */}
      <div className="band">
        <div className="shell">
          <CandleStrip count={34} seed={11} height={132} />
          <p className="band__note">
            Structural motion only. No candle here encodes a value — the same rule every panel in
            the product follows.
          </p>
        </div>
      </div>

      {/* ── Sections ─────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell">
          <Reveal as="p" className="eyebrow">
            The registry
          </Reveal>
          <Headline
            as="h2"
            className="display display--section section__title"
            text="Twenty sections, grouped by the question you are asking."
            highlight={['question']}
          />

          <div className="clusters">
            {v.clusters.map((cluster, i) => (
              <Reveal key={cluster.label} delay={i * 100}>
                <TiltCard className="cluster glass" max={3}>
                  <div className="cluster__head">
                    <h3>{cluster.label}</h3>
                    <span>{cluster.count}</span>
                  </div>
                  <p className="cluster__blurb">{cluster.blurb}</p>
                  <ul className="cluster__list">
                    {cluster.sections.map((s) => (
                      <li key={s.mnemonic}>
                        <code>{s.mnemonic}</code>
                        <span>{s.title}</span>
                        {s.badge ? <em>{s.badge}</em> : null}
                      </li>
                    ))}
                  </ul>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Record ───────────────────────────────────────────────────────── */}
      <section className="section">
        <div className="shell split">
          <div className="split__lead">
            <Reveal as="p" className="eyebrow">
              Track record
            </Reveal>
            <Headline
              as="h2"
              className="display display--section"
              text="Written down first, scored later, published either way."
              highlight={['scored']}
            />
            <Reveal as="p" className="lede" delay={200}>
              A forecast that can be edited after the outcome is not a forecast. Horizon and
              quantiles are fixed at write time, and the ledger keeps the misses next to the hits.
            </Reveal>
            <Reveal delay={320}>
              <Link to="/record" className="btn btn--ghost">
                Read the method
              </Link>
            </Reveal>
          </div>

          <div className="scores">
            {v.scoreCards.map((card, i) => (
              <Reveal key={card.label} delay={i * 90}>
                <div className="score glass">
                  <span className="score__value">—</span>
                  <span className="score__label">{card.label}</span>
                  <span className="score__note">{card.note}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Closing CTA ──────────────────────────────────────────────────── */}
      <section className="section closing">
        <div className="shell">
          <Reveal className="closing__inner glass">
            <Headline
              as="h2"
              className="display display--section"
              text="Open the terminal and see exactly what is bound."
              highlight={['bound.']}
            />
            <Reveal as="p" className="lede" delay={220}>
              Sign in to reach the dashboard. Panels with a connector draw; panels without one name
              the module that would feed them.
            </Reveal>
            <Reveal className="closing__actions" delay={340}>
              <a href="/cockpit" className="btn btn--primary">Open the terminal</a>
              <Link to="/access" className="btn btn--ghost">
                Run it yourself
              </Link>
            </Reveal>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
